import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Phone, LogOut, LayoutDashboard, Search, PackagePlus, Megaphone, BadgeCheck, Globe, Users, MessageCircle, HeartHandshake, Coins, ChevronDown, Store, Bot, Wrench, Newspaper, Building2, ShoppingBag, Tag, Cpu, Boxes } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { key: "marketplace", icon: Store, items: [
    { key: "catalogProducts", to: "/produse", icon: Boxes },
    { key: "machineExchange", to: "/anunturi", icon: PackagePlus },
    { key: "requests", to: "/cereri", icon: Megaphone },
  ] },
  { key: "aiExperts", icon: Bot, items: [
    { key: "advisor", to: "/consilier", icon: Cpu },
    { key: "experts", to: "/experti", icon: BadgeCheck },
    { key: "directory", to: "/directory", icon: Building2 },
  ] },
  { key: "communityImpact", icon: Users, items: [
    { key: "whatsappNetwork", to: "/comunitate", icon: MessageCircle },
    { key: "news", to: "/news", icon: Newspaper },
    { key: "arva", to: "/arva-szakmak", icon: HeartHandshake, highlight: true },
  ] },
  { key: "services", icon: Wrench, items: [
    { key: "installation", to: "/servicii#instalare", icon: Wrench },
    { key: "maintenance", to: "/servicii#mentenanta", icon: Wrench },
    { key: "logistics", to: "/servicii#logistica", icon: Boxes },
  ] },
];

const LANGS = ["RO", "HU", "EN"];

export default function Header() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const lang = (i18n.language || "ro").slice(0, 2).toUpperCase();
  const phoneRaw = t("footer_contact_phone");
  const telHref = `tel:${phoneRaw.replace(/\s/g, "")}`;
  const waHref = `https://wa.me/${phoneRaw.replace(/[^0-9]/g, "")}`;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="bg-slate-900 text-slate-300 text-xs">
        <div className="max-w-[1600px] mx-auto px-6 h-9 flex items-center justify-between gap-3">
          <span className="tracking-wide truncate min-w-0" data-testid="topbar-tagline">{t("logo_main_text")} · {t("hero_title")}</span>
          <a href={telHref} className="hidden sm:flex items-center gap-2 hover:text-orange-500 transition-colors shrink-0" data-testid="header-phone">
            <Phone className="w-3.5 h-3.5" /> {phoneRaw}
          </a>
          <div className="flex sm:hidden items-center gap-4 shrink-0" data-testid="topbar-mobile-actions">
            <a href={telHref} className="flex items-center gap-1.5 hover:text-orange-500 transition-colors" data-testid="topbar-call-mobile" aria-label={t("topbar_call")}>
              <Phone className="w-3.5 h-3.5" /> <span className="font-semibold">{t("topbar_call")}</span>
            </a>
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="topbar-whatsapp-mobile" aria-label="WhatsApp">
              <MessageCircle className="w-3.5 h-3.5" /> <span className="font-semibold">WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <div className="w-11 h-11 bg-orange-600 flex items-center justify-center">
            <span className="text-white font-black text-xl tracking-tighter">OXO</span>
          </div>
          <div className="hidden md:block leading-none">
            <div className="font-black text-lg tracking-tighter text-slate-900" data-testid="logo-main-text">{t("logo_main_text")}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="logo-tagline">{t("logo_tagline")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
          {NAV.map((cat) => (
            <div key={cat.key} className="relative group" data-testid={`nav-cat-${cat.key}`}>
              <button className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-slate-700 group-hover:text-orange-600 transition-colors">
                <cat.icon className="w-4 h-4" /> {t(`nav.${cat.key}`)}
                <ChevronDown className="w-3.5 h-3.5 opacity-60 group-hover:rotate-180 transition-transform" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 z-50">
                <div className="w-64 bg-white border border-slate-200 shadow-xl py-2">
                  {cat.items.map((it) => (
                    <NavLink key={it.to} to={it.to} data-testid={`nav-${it.key}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          it.highlight ? "text-emerald-700 font-bold hover:bg-emerald-50"
                          : isActive ? "text-orange-600 bg-orange-50 font-semibold" : "text-slate-700 hover:bg-slate-50 hover:text-orange-600"
                        }`}>
                      <it.icon className={`w-4 h-4 ${it.highlight ? "text-emerald-600" : "text-slate-400"}`} /> {t(`nav.${it.key}`)}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <NavLink to="/preturi" data-testid="nav-pricing"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${
                isActive ? "text-orange-600" : "text-slate-800 hover:text-orange-600"
              }`}>
            <Tag className="w-4 h-4" /> {t("nav.pricing")}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-slate-700 hover:text-orange-600 transition-colors px-2" data-testid="lang-switcher">
                <Globe className="w-4 h-4" /> {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l} onClick={() => i18n.changeLanguage(l.toLowerCase())} data-testid={`lang-${l}`}>
                  <span className={lang === l ? "font-bold text-orange-600" : ""}>{l === "RO" ? "Română" : l === "HU" ? "Magyar" : "English"}</span>
                  {lang === l && <span className="ml-auto text-orange-600">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/vinde" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white bg-orange-600 hover:bg-orange-700 transition-colors" data-testid="sell-machine-cta">
            <PackagePlus className="w-4 h-4" /> + {t("header_cta")}
          </Link>
          <Link to="/produse" className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-slate-900 border border-slate-300 hover:border-orange-600 hover:text-orange-600 transition-colors" data-testid="buy-cta">
            <ShoppingBag className="w-4 h-4" /> {t("nav.buy")}
          </Link>
          <button onClick={() => setSearchOpen((s) => !s)} className="p-2.5 hover:bg-slate-100 transition-colors" data-testid="header-search-toggle" aria-label="Căutare">
            {searchOpen ? <X className="w-5 h-5 text-slate-800" /> : <Search className="w-5 h-5 text-slate-800" />}
          </button>
          <Link to="/cos" className="relative p-2.5 hover:bg-slate-100 transition-colors" data-testid="cart-button">
            <ShoppingCart className="w-5 h-5 text-slate-800" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-orange-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="p-2.5 hover:bg-slate-100 transition-colors outline-none" data-testid="user-menu">
                <User className="w-5 h-5 text-slate-800" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-none">
                <div className="px-3 py-2 text-xs text-slate-500 truncate">{user.email}</div>
                <div className="px-3 pb-2 flex items-center justify-between gap-1.5 text-xs font-bold text-slate-700" data-testid="header-token-balance">
                  <span className="flex items-center gap-1.5"><Coins className="w-3.5 h-3.5 text-amber-500" /> {user.tokens_balance ?? 0} tokenuri</span>
                  <button onClick={() => navigate("/tokens")} className="text-orange-600 hover:underline uppercase tracking-wide" data-testid="menu-buy-tokens">+ Reîncarcă</button>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/cont")} data-testid="menu-account">
                  <User className="w-4 h-4 mr-2" /> {t("dropdown_my_account")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/anunturile-mele")} data-testid="menu-my-listings">
                  <Megaphone className="w-4 h-4 mr-2" /> {t("dropdown_my_listings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/cont-expert")} data-testid="menu-expert-profile">
                  <BadgeCheck className="w-4 h-4 mr-2" /> {t("dropdown_expert_profile")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/cont-sponsor")} data-testid="menu-sponsor-profile">
                  <Megaphone className="w-4 h-4 mr-2" /> {t("dropdown_become_partner")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Panou Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("dropdown_logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/autentificare" className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-100 transition-colors" data-testid="login-link">
              <User className="w-4 h-4" /> {t("nav.account")}
            </Link>
          )}

          <button className="lg:hidden p-2.5" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-slate-200 bg-slate-50" data-testid="header-search-panel">
          <div className="max-w-[1600px] mx-auto px-6 py-4">
            <SearchAutocomplete autoFocus onClose={() => setSearchOpen(false)} />
          </div>
        </div>
      )}

      {open && (
        <nav className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 flex flex-col gap-4 max-h-[75vh] overflow-y-auto" data-testid="mobile-nav">
          {NAV.map((cat) => (
            <div key={cat.key}>
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                <cat.icon className="w-3.5 h-3.5" /> {t(`nav.${cat.key}`)}
              </div>
              {cat.items.map((it) => (
                <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} data-testid={`nav-m-${it.key}`}
                  className={`flex items-center gap-2 py-2 pl-5 text-sm font-semibold ${it.highlight ? "text-emerald-700" : "text-slate-700"}`}>
                  <it.icon className={`w-4 h-4 ${it.highlight ? "text-emerald-600" : "text-slate-400"}`} /> {t(`nav.${it.key}`)}
                </NavLink>
              ))}
            </div>
          ))}
          <NavLink to="/preturi" onClick={() => setOpen(false)} data-testid="nav-pricing-mobile"
            className="py-3 text-sm font-bold uppercase tracking-wide text-slate-900 border-t border-slate-100 flex items-center gap-2">
            <Tag className="w-4 h-4 text-orange-600" /> {t("nav.pricing")}
          </NavLink>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Link to="/vinde" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-2 py-3 text-sm font-bold uppercase tracking-wide text-white bg-orange-600" data-testid="sell-cta-mobile">
              <PackagePlus className="w-4 h-4" /> + {t("header_cta")}
            </Link>
            <Link to="/produse" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-2 py-3 text-sm font-bold uppercase tracking-wide text-slate-900 border border-slate-300" data-testid="buy-cta-mobile">
              <ShoppingBag className="w-4 h-4" /> {t("nav.buy")}
            </Link>
          </div>
          <Link to={user ? "/cont" : "/autentificare"} onClick={() => setOpen(false)} className="py-3 text-sm font-semibold uppercase tracking-wide text-slate-700 border-t border-slate-100 flex items-center gap-2">
            <User className="w-4 h-4" /> {t("nav.account")}
          </Link>
        </nav>
      )}
    </header>
  );
}
